import React, { Component } from 'react';
import {
    Container,
    Header,
    Grid,
    Menu,
    Button,
    Table,
    Icon,
    Image,
    Pagination,
    Loader,
    Label,
    List
} from 'semantic-ui-react';
import { ajaxPost } from '../ajax';
import { checkDateRange, isTaskAvailable } from '../utils';

// Models
import MasterListReportModel from './ReportModels/MasterListReportModel';
import EmployeeReportModelGroupByGender from './ReportModels/EmployeeReport/EmployeeReportModelGroupByGender';
import EmployeeReportModelGroupByCivilStatus from './ReportModels/EmployeeReport/EmployeeReportModelGroupByCivilStatus';
import EmployeeReportModelGroupByBirthMonth from './ReportModels/EmployeeReport/EmployeeReportModelGroupByBirthMonth';
import EmployeeReportModelGroupByPosition from './ReportModels/EmployeeReport/EmployeeReportModelGroupByPosition';
import EmployeeReportModelGroupByDepartment from './ReportModels/EmployeeReport/EmployeeReportModelGroupByDepartment';
import EmployeeReportModelGroupByLocation from './ReportModels/EmployeeReport/EmployeeReportModelGroupByLocation';
import SeparatedEmployeeReportModel from './ReportModels/EmployeeReport/SeparatedEmployeeReportModel';
import EmployeeSubordinatesReportModel from './ReportModels/EmployeeReport/EmployeeSubordinatesReportModel';
import EmployeeTenureReportModel from './ReportModels/EmployeeReport/TenureReportModel';
import EmploymentHistoryReportModel from './ReportModels/EmployeeReport/EmploymentHistoryReportModel';
import ProbationaryEmployeeReportModel from './ReportModels/EmployeeReport/ProbationaryReportModel';
import RegularEmployeeReportModel from './ReportModels/EmployeeReport/RegularEmployeeReportModel';
import RehiredEmployeeReportModel from './ReportModels/EmployeeReport/RehiredEmployeeReportModel';

// Modals
import EmployeeFilter from '../Commons/EmployeeFilterModal'; // Used for Masterlist, Subordinates
import EmployeeReportModalGroupByGender from './ReportModels/EmployeeReport/EmployeeReportModalGroupByGender';
import EmployeeReportModalGroupByCivilStatus from './ReportModels/EmployeeReport/EmployeeReportModalGroupByCivilStatus';
import EmployeeReportModalGroupByBirthMonth from './ReportModels/EmployeeReport/EmployeeReportModalGroupByBirthMonth';
import EmployeeReportModalGroupByPosition from './ReportModels/EmployeeReport/EmployeeReportModalGroupByPosition';
import EmployeeReportModalGroupByDepartment from './ReportModels/EmployeeReport/EmployeeReportModalGroupByDepartment';
import EmployeeReportModalGroupByLocation from './ReportModels/EmployeeReport/EmployeeReportModalGroupByLocation';
import SeparatedEmployeeReportModal from './ReportModels/EmployeeReport/SeparatedEmployeeReportModal';
import EmployeeTenureReportModal from './ReportModels/EmployeeReport/TenureReportModal';
import EmploymentHistoryReportModal from './ReportModels/EmployeeReport/EmploymentHistoryReportModal';
import DateRangeModal from './ReportModels/EmployeeReport/DateRangeModal'; // For Regular, Probationary
import RehiredEmployeeReportModal from './ReportModels/EmployeeReport/RehiredEmployeeReportModal';

class EmployeeReportsModule extends Component {
    _isMounted = false;

    constructor(props) {
        super(props);
        this.state = {
            activeReport: 'Employee Masterlist',
            reportModel: null,
            rows: [],
            loading: false,
            isFilterActive: false,
            parameters: null,

            // Pagination
            activePage: 1,
            totalPages: 0,
            totalResultsCount: 0,
            itemsPerPage: 20,

            disAbleExport: true,
            advanceFilter: {
                employeeIds: [],
                companyIds: [],
                departmentIds: [],
                locationIds: [],
                positionIds: []
            },
        };

        this.filter = React.createRef();
        this.pollingTimer = null;
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.props.prevState) {
            this.restoreState(this.props.prevState);
        } else {
            this.setReportModel(this.state.activeReport);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
        }
        if (this.props.unmountCallback) {
            const stateToSave = { ...this.state };
            delete stateToSave.reportModel;
            this.props.unmountCallback(stateToSave, "EmployeeReportsModule");
        }
    }

    restoreState = (prevState) => {
        const reportModel = this.createReportModel(prevState.activeReport);
        this.setState({
            ...prevState,
            reportModel
        });
    }

    createReportModel = (reportType) => {
        switch (reportType) {
            case 'Employee Masterlist': return new MasterListReportModel();
            case 'Employee Report Group by Birth Month': return new EmployeeReportModelGroupByBirthMonth();
            case 'Employee Report Group by Civil Status': return new EmployeeReportModelGroupByCivilStatus();
            case 'Employee Report Group by Department': return new EmployeeReportModelGroupByDepartment();
            case 'Employee Report Group by Gender': return new EmployeeReportModelGroupByGender();
            case 'Employee Report Group by Location': return new EmployeeReportModelGroupByLocation();
            case 'Employee Report Group by Position': return new EmployeeReportModelGroupByPosition();
            case 'Employee Separation Report': return new SeparatedEmployeeReportModel();
            case 'Employee Subordinates Report': return new EmployeeSubordinatesReportModel();
            case 'Employee Tenure Report': return new EmployeeTenureReportModel();
            case 'Employment History': return new EmploymentHistoryReportModel();
            case 'Probationary Employee Report': return new ProbationaryEmployeeReportModel();
            case 'Regular Employee Report': return new RegularEmployeeReportModel();
            case 'Rehired Employee Report': return new RehiredEmployeeReportModel();
            default: return null;
        }
    }

    setReportModel = (reportType) => {
        const reportModel = this.createReportModel(reportType);

        if (this._isMounted) {
            this.setState({
                reportModel,
                activeReport: reportType,
                rows: [],
                totalResultsCount: 0,
                totalPages: 0,
                activePage: 1,
                disAbleExport: true,
                parameters: null,
                isFilterActive: false
            });
        }
    };

    handleReportClick = (report) => {
        this.setReportModel(report);
    };

    showFilters = () => {
        if (this.state.loading) {
            alert("Please wait until the process is done.");
            return;
        }

        this.setState({ isFilterActive: true });
    }

    // --- Parameter Generation Helpers (Ported from ReportBase) ---
    // Note: Some rely on this.filter.current.getParameters()

    btnGenerate_Click = () => {
        if (!this.filter.current) return;
        const params = this.filter.current.getParameters();
        this.runSearchWithParams(params);
    }

    btnGenerateMasterlist = (ids, includeDeleted, inactiveOnly) => {
        const params = {
            ...this.advanceFilterGroupParams(),
            includeDeleted: includeDeleted,
            inactiveOnly: inactiveOnly,
            message: "from masterlist modal",
            employeeIds: ids === null ? undefined : ids.length === 0 ? undefined : JSON.parse(JSON.stringify(ids)),
            Search: "",
            startingIndex: 0,
            itemCount: 20
        };
        this.runSearchWithParams(params);
    }

    btnGenerateSeparatedEmployee = () => {
        // Separated modal likely returns params via getParameters
        this.btnGenerate_Click();
    }

    btnGenerateEmploymentHistory = (ids, filterGroup) => {
        const params = {
            ...filterGroup,
            message: "from employment history modal",
            employeeIds: ids === null || ids.length === 0 ? undefined : JSON.parse(JSON.stringify(ids)),
            startingIndex: 0,
            itemCount: 20
        };
        this.runSearchWithParams(params);
    }

    btnGenerateEmployeeSubordinates = (ids, includeDeleted) => {
        const params = {
            ...this.advanceFilterGroupParams(),
            includeDeleted: includeDeleted,
            message: "from employee subordinates modal",
            employeeIds: ids.length === 0 ? undefined : JSON.parse(JSON.stringify(ids)),
            Search: "",
            startingIndex: 0,
            itemCount: 20
        };
        this.runSearchWithParams(params);
    }

    runSearchWithParams = (params) => {
        if (!params) {
            alert("Select filter parameter first.");
            return;
        }

        // Validate Date Range if present (used in Regular/Probationary Employee Reports)
        if (params.fromDate !== undefined && params.toDate !== undefined) {
            var checkDateParam = checkDateRange(params.fromDate, params.toDate);
            if (!checkDateParam.Result) {
                alert(checkDateParam.Message);
                return;
            }
        }

        this.setState({ parameters: params, isFilterActive: false, activePage: 1 }, () => {
            setTimeout(() => {
                if (this._isMounted) {
                    this.setState({ disAbleExport: false }, this.onSearch);
                }
            }, 500);
        });
    }

    onSearch = () => {
        this.setState({ rows: [], loading: true });
        const { parameters, activePage, itemsPerPage, reportModel, activeReport } = this.state;

        let searchParams = { ...parameters };
        searchParams.pageNumber = activePage;
        searchParams.itemsPerPage = itemsPerPage;
        searchParams.ReportTitle = activeReport;

        // logic for startingIndex based on ReportBase line 409
        if (searchParams.message && (
            searchParams.message === "from masterlist modal" ||
            searchParams.message === "from employee report group by gender modal" ||
            searchParams.message === "from employee report group by civil status modal" ||
            searchParams.message === "from employee report group by birth month modal" ||
            searchParams.message === "from employee report group by position modal" ||
            searchParams.message === "from employee report group by department modal" ||
            searchParams.message === "from employee report group by location modal" ||
            searchParams.message === "from separated employee modal" ||
            searchParams.message === "from employee subordinates modal" ||
            searchParams.message === "from employee tenure report modal" ||
            searchParams.message === "from rehired employee modal" ||
            searchParams.message === "from employment history modal"
        )) {
            searchParams.startingIndex = (activePage - 1) * 20;
        }

        const backgroundReports = ["Regular Employee Report", "Probationary Employee Report", "Perfect Attendance Report"];

        reportModel.search(
            searchParams,
            (data) => {
                if (backgroundReports.includes(activeReport)) {
                    var param = { processId: data };
                    this.startPolling(param, "GENERATE");
                } else {
                    this.populateRows(data);
                }
            },
            (error) => {
                alert("Error:\r\n" + JSON.stringify(error));
                if (this._isMounted) this.setState({ loading: false });
            },
            () => { }
        );
    }

    populateRows = (data) => {
        let rows = [];
        if (data.Data) {
            data.Data.forEach(element => {
                rows.push(this.state.reportModel.toTableRow(element));
            });
        }

        // Handle null/undefined TotalCount - default to 0
        const totalCount = data.TotalCount ?? 0;

        let totalPages = 0;
        if (this.state.itemsPerPage > 0 && totalCount > 0) {
            totalPages = Math.ceil(totalCount / this.state.itemsPerPage);
        }

        // Ensure at least 1 page to prevent invalid pagination display
        if (totalPages === 0) {
            totalPages = 1;
        }

        if (this._isMounted) {
            this.setState({
                rows: rows,
                totalResultsCount: totalCount,
                totalPages: totalPages,
                loading: false
            });
        }
    }

    onPageChange = (event, { activePage }) => {
        const page = Math.floor(activePage);
        if (page === this.state.activePage) return;

        this.setState({ activePage: page }, () => {
            if (!this.state.disAbleExport) {
                this.onSearch();
            }
        });
    }

    onExportToExcel = () => {
        const { reportModel, loading, parameters, activeReport } = this.state;
        if (!reportModel) return;

        const hasExportMasterList = isTaskAvailable("EMPLOYEE_EXPORT_MASTER_LIST");
        if ((hasExportMasterList === false && activeReport === "Employee Masterlist")) {
            alert("Access Denied");
            return;
        }

        if (loading) {
            alert("Please wait until the process is done.");
            return;
        }

        this.setState({ loading: true });
        let exportParams = { ...parameters };
        exportParams.pageNumber = 0;
        exportParams.itemsPerPage = 2147483647;
        exportParams.ReportTitle = activeReport;
        exportParams.sessionId = global.sessionId;

        const handleError = (error) => {
            console.error("Export Error:", error);
            const msg = error.message || (typeof error === 'string' ? error : JSON.stringify(error));
            alert("Error:\r\n" + msg);
            if (this._isMounted) this.setState({ loading: false });
        };

        if (activeReport === "Employee Masterlist") {
            reportModel.masterlistExportToExcel(
                exportParams,
                (data) => { },
                handleError,
                () => { if (this._isMounted) this.setState({ loading: false }); }
            );
            return;
        }

        const employeeReportSubset = ["Regular Employee Report", "Probationary Employee Report"];

        if (employeeReportSubset.includes(activeReport)) {
            reportModel.exportToExcelData(exportParams,
                (data) => {
                    var param = { processId: data };
                    this.startPolling(param, "GENERATE_EXCEL");
                },
                handleError,
                () => { }
            );
        } else {
            // Others
            reportModel.exportToExcel(
                { ...exportParams, itemCount: 2147483647 },
                (d) => { if (this._isMounted) this.setState({ loading: false }); },
                handleError,
                () => { }
            );
        }
    }

    startPolling = (param, action) => {
        if (this.pollingTimer) clearInterval(this.pollingTimer);
        this.pollingTimer = setInterval(() => {
            if (!this._isMounted) { clearInterval(this.pollingTimer); return; }
            ajaxPost({
                data: param,
                url: 'api/BackgroundProcess/GetStatus',
                onSuccess: data => {
                    if (data.Status === "DONE") {
                        clearInterval(this.pollingTimer);
                        if (this._isMounted) {
                            if (action === "GENERATE") {
                                this.populateRows(JSON.parse(data.ResultParameters));
                            }
                            else if (action === "GENERATE_EXCEL") {
                                this.state.reportModel.exportToExcel(JSON.parse(data.ResultParameters), d => { }, e => { }, () => { });
                                this.setState({ loading: false });
                            }
                            else if (action === "GENERATE_PDF") {
                                // PDF Logic
                                if (this.state.activeReport === "Regular Employee Report" || this.state.activeReport === "Probationary Employee Report") {
                                    this.state.reportModel.processSTDPdfData(JSON.parse(data.ResultParameters));
                                } else {
                                    this.state.reportModel.processPdfData(JSON.parse(data.ResultParameters));
                                }
                                this.setState({ loading: false });
                            }
                            // Cleanup
                            ajaxPost({ data: param, url: 'api/BackgroundProcess/Delete', onSuccess: () => { }, onError: () => { }, finally: () => { } });
                        }
                    }
                },
                onError: (e) => {
                    clearInterval(this.pollingTimer);
                    console.error("Polling Error:", e);
                    const msg = e.message || (typeof e === 'string' ? e : JSON.stringify(e));
                    alert("Error status:\r\n" + msg);
                    if (this._isMounted) this.setState({ loading: false });
                }
            });
        }, 1000);
    }

    onExportToPdf = () => {
        const { reportModel, loading, parameters, activeReport } = this.state;
        if (!reportModel) return;
        const hasExportMasterList = isTaskAvailable("EMPLOYEE_EXPORT_MASTER_LIST");
        if ((hasExportMasterList === false && activeReport === "Employee Masterlist")) {
            alert("Access Denied");
            return;
        }
        if (loading) {
            alert("Please wait until the process is done.");
            return;
        }

        this.setState({ loading: true });
        let exportParams = { ...parameters };
        // exportParams.pageNumber = this.state.activePage;
        // exportParams.itemsPerPage = this.state.itemsPerPage;
        exportParams.ReportTitle = activeReport;
        exportParams.itemCount = 2147483647;
        exportParams.itemsPerPage = 2147483647;
        exportParams.pageNumber = 0;

        reportModel.exportToPdf(
            exportParams,
            (data) => {
                // Determine how to process
                if (activeReport === "Employee Masterlist") {
                    reportModel.employeeMasterlistPdfData(data);
                    if (this._isMounted) this.setState({ loading: false });
                }
                else if (["Employee Report Group by Gender", "Employee Report Group by Civil Status", "Employee Report Group by Birth Month",
                    "Employee Report Group by Position", "Employee Report Group by Department", "Employee Report Group by Location",
                    "Employee Separation Report", "Employee Subordinates Report", "Employee Tenure Report", "Rehired Employee Report",
                    "Employment History"].includes(activeReport)) {
                    reportModel.employeeReportWithoutDate(data);
                    if (this._isMounted) this.setState({ loading: false });
                }
                else if (["Regular Employee Report", "Probationary Employee Report"].includes(activeReport)) {
                    reportModel.processSTDPdfData(data);
                    if (this._isMounted) this.setState({ loading: false });
                }
                else {
                    // Fallback
                    reportModel.processPdfData(data);
                    if (this._isMounted) this.setState({ loading: false });
                }
            },
            (error) => {
                alert("Error:\r\n" + JSON.stringify(error));
                if (this._isMounted) this.setState({ loading: false });
            },
            () => { }
        );
    }

    // Employee Filter Callback: org-unit filters are resolved server-side (Filter Group),
    // only the hand-picked employees are passed as ids.
    onAdvanceEmployeeSearchFilter = (advanceFilter, includeDeleted, inactiveOnly, callback, startDate, endDate) => {
        var employees = !(advanceFilter.employeeIds === undefined || advanceFilter.employeeIds === null) ? advanceFilter.employeeIds.map(x => x.value) : [];
        this.setState({ advanceFilter }, () => {
            callback(employees, includeDeleted, inactiveOnly, startDate, endDate);
        });
    }

    advanceFilterGroupParams = () => {
        const adv = this.state.advanceFilter || {};
        return {
            companyIds: adv.companyIds || [],
            divisionIds: adv.divisionIds || [],
            departmentIds: adv.departmentIds || [],
            positionIds: adv.positionIds || [],
            locationIds: adv.locationIds || [],
            costCenterIds: adv.costcenterIds || [],
        };
    }

    renderFilters = () => {
        const { activeReport, isFilterActive } = this.state;
        if (!isFilterActive) return null;

        const commonProps = {
            ref: this.filter,
            open: true,
            onSave: this.btnGenerate_Click,
            onCancel: () => this.setState({ isFilterActive: false })
        };

        if (activeReport === "Employee Masterlist") {
            return (
                <EmployeeFilter
                    {...commonProps}
                    advFilter={this.state.advanceFilter}
                    onGenerateCallback={this.btnGenerateMasterlist}
                    callback={this.onAdvanceEmployeeSearchFilter}
                    showFilter={["INACTIVE_ONLY"]}
                    onSave={undefined} // EmployeeFilter uses onGenerateCallback
                />
            );
        }

        if (activeReport === "Employee Subordinates Report") {
            return (
                <EmployeeFilter
                    {...commonProps}
                    advFilter={this.state.advanceFilter}
                    onGenerateCallback={this.btnGenerateEmployeeSubordinates}
                    callback={this.onAdvanceEmployeeSearchFilter}
                    onSave={undefined}
                />
            );
        }

        if (activeReport === "Employment History") {
            return (
                <EmploymentHistoryReportModal
                    {...commonProps}
                    callback={this.btnGenerateEmploymentHistory}
                    onSave={undefined} // Modal might use callback
                />
            );
        }

        if (activeReport === "Regular Employee Report" || activeReport === "Probationary Employee Report") {
            return (
                <DateRangeModal
                    title={activeReport}
                    {...commonProps}
                />
            );
        }

        switch (activeReport) {
            case 'Employee Report Group by Gender': return <EmployeeReportModalGroupByGender {...commonProps} />;
            case 'Employee Report Group by Civil Status': return <EmployeeReportModalGroupByCivilStatus {...commonProps} />;
            case 'Employee Report Group by Birth Month': return <EmployeeReportModalGroupByBirthMonth {...commonProps} />;
            case 'Employee Report Group by Position': return <EmployeeReportModalGroupByPosition {...commonProps} />;
            case 'Employee Report Group by Department': return <EmployeeReportModalGroupByDepartment {...commonProps} />;
            case 'Employee Report Group by Location': return <EmployeeReportModalGroupByLocation {...commonProps} />;
            case 'Employee Separation Report': return <SeparatedEmployeeReportModal {...commonProps} onSave={this.btnGenerateSeparatedEmployee} />;
            case 'Employee Tenure Report': return <EmployeeTenureReportModal {...commonProps} />;
            case 'Rehired Employee Report': return <RehiredEmployeeReportModal {...commonProps} />;
            default: return null;
        }
    }

    isExportDisabled = () => {
        const { disAbleExport, activeReport } = this.state;
        if (activeReport === "Employee Masterlist") {
            const hasPermission = isTaskAvailable("EMPLOYEE_EXPORT_MASTER_LIST");
            if (!hasPermission) return true;
        }
        return disAbleExport;
    }

    render() {
        const { activeReport, rows, loading, totalResultsCount, activePage, totalPages } = this.state;

        const reports = [
            'Employee Masterlist',
            'Employee Report Group by Birth Month',
            'Employee Report Group by Civil Status',
            'Employee Report Group by Department',
            'Employee Report Group by Gender',
            'Employee Report Group by Location',
            'Employee Report Group by Position',
            'Employee Separation Report',
            'Employee Subordinates Report',
            'Employee Tenure Report',
            'Employment History',
            'Probationary Employee Report',
            'Regular Employee Report',
            'Rehired Employee Report'
        ];

        return (
            <Container fluid>
                <Header as="h2" style={{ marginBottom: '16px' }}>
                    Employee Reports
                </Header>

                <Grid>
                    <Grid.Column width={3}>
                        <List selection verticalAlign='middle'>
                            {reports.map((report) => {
                                const isActive = activeReport === report;
                                const style = {
                                    cursor: 'pointer',
                                    color: isActive ? '#000' : '#888',
                                    fontWeight: isActive ? 600 : 400,
                                    padding: '10px',
                                    backgroundColor: isActive ? '#f0f0f0' : 'transparent',
                                    borderRadius: '4px'
                                };
                                return (
                                    <List.Item key={report} onClick={() => this.handleReportClick(report)} style={style}>
                                        <List.Content>
                                            <List.Header>{report}</List.Header>
                                        </List.Content>
                                    </List.Item>
                                );
                            })}
                        </List>
                    </Grid.Column>

                    <Grid.Column width={13}>
                        <div style={{ padding: 0, marginTop: '1em', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 150px)' }}>
                            <Header as="h3">{activeReport}</Header>

                            <div style={{ zIndex: 1000, position: 'relative', margin: '0.5em 0.1em' }}>
                                <Menu secondary size="mini" stackable>
                                    <Button size="mini" content="Generate" onClick={this.showFilters} />
                                    <Button size="mini" content="Export To Excel" disabled={this.isExportDisabled()} onClick={this.onExportToExcel} />
                                    <Button size="mini" content="Export To PDF" disabled={this.isExportDisabled()} onClick={this.onExportToPdf} />
                                    <Loader active={loading} inline='left' style={{ margin: '0 0.5em' }}  />
                                    
                                    <Menu.Menu position="right">
                                        <Label>Total Results Count: {totalResultsCount}</Label>
                                        <Pagination
                                            size="mini"
                                            activePage={activePage}
                                            totalPages={totalPages}
                                            onPageChange={this.onPageChange}
                                            firstItem={{ content: <Icon name='angle double left' />, icon: true }}
                                            lastItem={{ content: <Icon name='angle double right' />, icon: true }}
                                            prevItem={{ content: <Icon name='angle left' />, icon: true }}
                                            nextItem={{ content: <Icon name='angle right' />, icon: true }}
                                        />
                                    </Menu.Menu>
                                </Menu>
                            </div>

                            <div style={{ flex: 1, overflow: 'auto', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px' }}>
                                <Table celled striped compact size='small' style={{ border: 'none' }}>
                                    <Table.Header>
                                        {this.state.reportModel && this.state.reportModel.createColumnHeaders()}
                                    </Table.Header>
                                    <Table.Body>
                                        {rows}
                                    </Table.Body>
                                </Table>
                            </div>
                        </div>
                    </Grid.Column>
                </Grid>
                {this.renderFilters()}
            </Container>
        );
    }
}

EmployeeReportsModule.getViewID = () => {
    return "EmployeeReportsModule";
};

EmployeeReportsModule.getCaption = () => {
    return "Employee Reports";
};

EmployeeReportsModule.getViewAccessKey = () => {
    return isTaskAvailable("HR_VIEW")  ? true : false
};

EmployeeReportsModule.getIcon = () => {
    return (<Image style={{ height: "24px", width: "24px" }} src="/Icons/Reports.png" avatar />);
};

EmployeeReportsModule.getIconSrc = (size) => {
    return '/Icons/Reports.png';
};

export default EmployeeReportsModule;
